import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { useEffect, useState } from 'react'
// import useLocalStorage from '../../hooks/useLocalStorage'
import { SortableItem } from './SortableItem'
import CustomSection from './CustomSection'
import SectionFilter from './SectionFilter'
import resetIcon from '../assets/reset.svg'

const kebabCaseToTitleCase = (str) =>
  str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

export const SectionsColumn = ({
  selectedSectionSlugs,
  setSelectedSectionSlugs,
  sectionSlugs,
  setSectionSlugs,
  setFocusedSectionSlug,
  focusedSectionSlug,
  templates,
  originalTemplate,
  setTemplates,
  getTemplate,
}) => {
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )
// eslint-disable-next-line
  const [pageRefreshed, setPageRefreshed] = useState(false)
  // eslint-disable-next-line
  const [addAction, setAddAction] = useState(false)
  const [searchFilter, setSearchFilter] = useState('')
  const [filteredSlugs, setFilteredSlugs] = useState([])
  // const { saveBackup, deleteBackup } = useLocalStorage()

  useEffect(() => {
    const stored = localStorage.getItem('current-slug-list')
    if (stored) {
      const slugList = stored.split(',')
      setSelectedSectionSlugs(slugList)
      setFocusedSectionSlug(slugList[0])
      setPageRefreshed(true)
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    localStorage.setItem('current-slug-list', selectedSectionSlugs)
  }, [selectedSectionSlugs])

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return

    setSelectedSectionSlugs((items) => {
      const oldIndex = items.indexOf(active.id)
      const newIndex = items.indexOf(over.id)
      return arrayMove(items, oldIndex, newIndex)
    })
  }

  const onAddSection = (_, section) => {
    setSectionSlugs((prev) => prev.filter((s) => s !== section))
    setSelectedSectionSlugs((prev) => [...prev, section])
    setFocusedSectionSlug(section)
    setSearchFilter('')
    setAddAction(true)
  }

  const onDeleteSection = (e, slug) => {
    e.stopPropagation()
    setSelectedSectionSlugs((prev) => prev.filter((s) => s !== slug))
    setSectionSlugs((prev) => [...prev, slug])
    setFocusedSectionSlug(null)
  }

  const onResetSection = (e, slug) => {
    e.stopPropagation()

    let original =
      slug.startsWith('custom')
        ? {
            slug,
            name: kebabCaseToTitleCase(slug.replace('custom', '')),
            markdown: `## ${kebabCaseToTitleCase(slug.replace('custom', ''))}`,
          }
        : originalTemplate.find((s) => s.slug === slug)

    const updated = templates.map((s) =>
      s.slug === slug ? original : s
    )

    setTemplates(updated)
    // saveBackup(updated)
  }

  const resetSelectedSections = () => {
    if (window.confirm('All sections will be removed. Continue?')) {
      setSelectedSectionSlugs(['title-and-description'])
      setFocusedSectionSlug('title-and-description')
      setTemplates(originalTemplate)
      // deleteBackup()
    }
  }

  useEffect(() => {
    if (!searchFilter) {
      setFilteredSlugs([])
      return
    }

    setFilteredSlugs(
      sectionSlugs.filter((slug) =>
        getTemplate(slug)?.name
          .toLowerCase()
          .includes(searchFilter.toLowerCase())
      )
    )
    // eslint-disable-next-line
  }, [searchFilter])

  const visibleSlugs = filteredSlugs.length ? filteredSlugs : sectionSlugs

  return (
    <div className="w-80">
      <h3 className="px-2 text-sm font-semibold text-emerald-600 flex justify-between">
        Sections
        <button onClick={resetSelectedSections}>
          <img src={resetIcon} alt="reset" width={16} />
        </button>
      </h3>

      <div className="px-3 overflow-y-scroll full-screen">
        <h4 className="text-xs text-gray-700 mb-2">Click on a section below to edit the contents</h4>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={selectedSectionSlugs}>
            {selectedSectionSlugs.map((slug) => {
              const template = getTemplate(slug)
              return (
                template && (
                  <SortableItem
                    key={slug}
                    id={slug}
                    section={template}
                    focusedSectionSlug={focusedSectionSlug}
                    setFocusedSectionSlug={setFocusedSectionSlug}
                    onDeleteSection={onDeleteSection}
                    onResetSection={onResetSection}
                  />
                )
              )
            })}
          </SortableContext>
        </DndContext>

        <h4 className="text-xs text-gray-700 mt-6 mb-2">Click on a section below to add it to your readme</h4>

        <SectionFilter
          searchFilter={searchFilter}
          setSearchFilter={setSearchFilter}
        />

        <CustomSection
          setSelectedSectionSlugs={setSelectedSectionSlugs}
          setFocusedSectionSlug={setFocusedSectionSlug}
          setPageRefreshed={setPageRefreshed}
          setAddAction={setAddAction}
          setTemplates={setTemplates}
        />

        <ul className="space-y-2 mt-4">
          {visibleSlugs.map((slug) => {
            const template = getTemplate(slug)
            return (
              template && (
                <li key={slug}>
                  <button
                    className="w-full text-left px-3 py-2 bg-white border rounded hover:bg-gray-100"
                    onClick={(e) => onAddSection(e, slug)}
                  >
                    {template.name}
                  </button>
                </li>
              )
            )
          })}
        </ul>
      </div>
    </div>
  )
}
